import { defineComponent, h, onBeforeUnmount, onMounted, useAttrs } from 'vue'
import { VDataTable, VTable } from 'vuetify/components'

if (VTable) {
  VTable.compatConfig = {
    ...(VTable.compatConfig || {}),
    INSTANCE_ATTRS_CLASS_STYLE: false
  }
}

function normalizeHeader(header) {
  if (header == null || typeof header !== 'object' || Array.isArray(header)) {
    return header
  }

  const value = header.value ?? header.key
  const text = header.text ?? header.title
  const isExpandHeader = value === 'data-table-expand'

  return {
    ...header,
    title: header.title ?? text,
    key: header.key ?? value,
    text,
    value,
    ...(isExpandHeader
      ? {
          width: header.width ?? 1,
          minWidth: header.minWidth ?? header.width ?? 1
        }
      : {})
  }
}

function normalizeHeaders(headers) {
  return Array.isArray(headers) ? headers.map(normalizeHeader) : headers
}

function hasShowExpand(attrs = {}) {
  return (
    attrs.showExpand === '' ||
    attrs.showExpand === true ||
    attrs.showExpand === 'true' ||
    attrs['show-expand'] === '' ||
    attrs['show-expand'] === true ||
    attrs['show-expand'] === 'true'
  )
}

function hasExpandHeader(headers) {
  return (
    Array.isArray(headers) &&
    headers.some((header) => {
      const value = header?.value ?? header?.key
      return value === 'data-table-expand'
    })
  )
}

function normalizeHeadersForAttrs(headers, attrs = {}) {
  if (!Array.isArray(headers)) return headers

  const legacyHeaders =
    hasShowExpand(attrs) && !hasExpandHeader(headers)
      ? [{ text: '', value: 'data-table-expand' }, ...headers]
      : headers

  return normalizeHeaders(legacyHeaders)
}

function normalizeWidthValue(width) {
  if (width == null || width === '') return undefined
  if (typeof width === 'number') return `${width}px`
  return /^\d+(\.\d+)?$/.test(String(width)) ? `${width}px` : width
}

function normalizeHeaderAlignClass(header = {}) {
  const align = header.align || 'start'
  return `text-${align}`
}

export function normalizeLegacyHeaderClass(header = {}) {
  return [
    normalizeHeaderAlignClass(header),
    header.sortable === false ? null : 'sortable',
    header.class,
    header.headerProps?.class
  ].filter(Boolean)
}

export function normalizeLegacyHeaderStyle(header = {}) {
  if ((header.key ?? header.value) === 'data-table-expand') {
    return {
      width: '1px',
      minWidth: '1px',
      ...(header.headerProps?.style || {})
    }
  }

  const width = normalizeWidthValue(header.width)
  const minWidth = normalizeWidthValue(header.minWidth ?? header.width)
  const maxWidth = normalizeWidthValue(header.maxWidth)

  return {
    ...(width ? { width } : {}),
    ...(minWidth ? { minWidth } : {}),
    ...(maxWidth ? { maxWidth } : {}),
    ...(header.headerProps?.style || {})
  }
}

export function hasLegacyHeaderPresentation(headers) {
  return (
    Array.isArray(headers) &&
    headers.some((header) => {
      return (
        header?.width ||
        header?.minWidth ||
        header?.maxWidth ||
        header?.class ||
        header?.align
      )
    })
  )
}

function findLegacyHeader(column, headers = []) {
  return headers.find((header) => {
    const key = header?.key ?? header?.value
    return key === column?.key || key === column?.value
  })
}

function normalizeItem(item) {
  const raw = item?.raw ?? item
  return raw
}

function stripLegacyAttrs(attrs) {
  const normalized = { ...attrs }
  const itemsPerPage = attrs.itemsPerPage ?? attrs['items-per-page']
  delete normalized.class
  delete normalized.headers
  delete normalized['item-key']
  delete normalized.itemKey
  delete normalized.expanded
  delete normalized['expanded.sync']
  delete normalized.sortBy
  delete normalized['sort-by']
  delete normalized.sortDesc
  delete normalized['sort-desc']
  if (normalized.height === 'auto' || Number(itemsPerPage) === -1) {
    delete normalized.height
  }
  return normalized
}

function isAllItemsTable(attrs = {}) {
  const itemsPerPage = attrs.itemsPerPage ?? attrs['items-per-page']
  return Number(itemsPerPage) === -1
}

function hasDisableSort(attrs = {}) {
  return (
    attrs.disableSort === '' ||
    attrs.disableSort === true ||
    attrs.disableSort === 'true' ||
    attrs['disable-sort'] === '' ||
    attrs['disable-sort'] === true ||
    attrs['disable-sort'] === 'true'
  )
}

function normalizeSortDesc(sortDesc) {
  if (Array.isArray(sortDesc)) return Boolean(sortDesc[0])
  return sortDesc === '' || sortDesc === true || sortDesc === 'true'
}

export function normalizeSortBy(attrs = {}) {
  const sortBy = attrs.sortBy ?? attrs['sort-by']
  const sortDesc = attrs.sortDesc ?? attrs['sort-desc']

  if (Array.isArray(sortBy)) {
    return sortBy.map((sortItem, index) => {
      if (sortItem == null || typeof sortItem !== 'object') {
        return {
          key: sortItem,
          order: Array.isArray(sortDesc) && sortDesc[index] ? 'desc' : 'asc'
        }
      }

      return {
        ...sortItem,
        key: sortItem.key ?? sortItem.value,
        order:
          sortItem.order ??
          (Array.isArray(sortDesc) && sortDesc[index] ? 'desc' : 'asc')
      }
    })
  }

  if (typeof sortBy === 'string' && sortBy) {
    return [
      {
        key: sortBy,
        order: normalizeSortDesc(sortDesc) ? 'desc' : 'asc'
      }
    ]
  }

  return undefined
}

export function normalizeDataTableClass(attrs = {}) {
  return [
    attrs.class,
    {
      'v-data-table--legacy-all-items': isAllItemsTable(attrs)
    }
  ]
}

function normalizeRowClass(rowProps = {}) {
  return rowProps.class ?? rowProps.className
}

function normalizeExpandedCandidate(candidate) {
  return candidate?.raw ?? candidate
}

function valuesMatch(left, right, itemValue) {
  const normalizedLeft = normalizeExpandedCandidate(left)
  const normalizedRight = normalizeExpandedCandidate(right)

  if (normalizedLeft === normalizedRight) return true
  if (itemValue == null || itemValue === '') return false

  return (
    normalizedLeft?.[itemValue] === normalizedRight ||
    normalizedLeft === normalizedRight?.[itemValue] ||
    normalizedLeft?.[itemValue] === normalizedRight?.[itemValue]
  )
}

export function isLegacyRowExpanded(scope = {}, attrs = {}) {
  const expanded = attrs.expanded
  const itemValue = attrs.itemValue ?? attrs.itemKey ?? attrs['item-key']
  const internalItem = scope.internalItem ?? scope.item

  if (scope.isExpanded?.(internalItem)) return true

  if (!Array.isArray(expanded)) return false

  return expanded.some(
    (expandedItem) =>
      valuesMatch(expandedItem, scope.internalItem?.value, itemValue) ||
      valuesMatch(expandedItem, scope.item, itemValue) ||
      valuesMatch(expandedItem, scope.internalItem, itemValue)
  )
}

export function shouldForwardLegacyExpanded(attrs = {}) {
  const expanded = attrs.expanded

  if (!Array.isArray(expanded)) return false

  // Vuetify 2 allowed `:expanded="[]"` as an initial empty state while still
  // letting the table manage click expansion. Vuetify 3 treats it as a
  // controlled model, which locks rows closed unless the parent syncs updates.
  return expanded.length > 0
}

export function normalizeLegacyRowProps(scope = {}, attrs = {}) {
  const configuredRowProps = attrs.rowProps ?? attrs['row-props']
  const legacyRowProps =
    typeof configuredRowProps === 'function'
      ? configuredRowProps(scope)
      : configuredRowProps
  const rowProps =
    legacyRowProps && typeof legacyRowProps === 'object' ? legacyRowProps : {}

  return {
    ...rowProps,
    class: [
      normalizeRowClass(rowProps),
      {
        'v-data-table__expanded v-data-table__expanded__row':
          isLegacyRowExpanded(scope, attrs)
      }
    ]
  }
}

export function normalizeDataTableFooterAttrs(attrs = {}) {
  const itemsPerPage = attrs.itemsPerPage ?? attrs['items-per-page']
  const normalized = {}

  if (attrs.itemsPerPageText == null && attrs['items-per-page-text'] == null) {
    normalized.itemsPerPageText = 'Rows per page:'
  }

  if (
    Number(itemsPerPage) === -1 &&
    attrs.itemsPerPageOptions == null &&
    attrs['items-per-page-options'] == null
  ) {
    normalized.itemsPerPageOptions = [
      { value: 5, title: '5' },
      { value: 10, title: '10' },
      { value: 15, title: '15' },
      { value: -1, title: 'All' }
    ]
  }

  return normalized
}

function callLegacyHandler(handler, event) {
  if (Array.isArray(handler)) {
    handler.forEach((fn) => callLegacyHandler(fn, event))
    return
  }

  if (typeof handler === 'function') {
    handler(event)
  }
}

function openLegacyFooterSelect(event) {
  const target = event.target
  const footerSelect = target?.closest?.('.v-data-table-footer__items-per-page')

  if (!footerSelect) return

  window.setTimeout(() => {
    const field = footerSelect.querySelector?.('.v-field')

    if (field?.getAttribute('aria-expanded') === 'true') return

    const input = footerSelect.querySelector?.('input[role="combobox"]')

    if (!input) return

    input.focus()
    input.dispatchEvent(
      new KeyboardEvent('keydown', {
        bubbles: true,
        cancelable: true,
        code: 'ArrowDown',
        key: 'ArrowDown',
        keyCode: 40,
        which: 40
      })
    )
  }, 0)
}

function closeLegacyFooterSelect() {
  const footerSelect = document
    .querySelector?.(
      '.i-data-table .v-data-table-footer__items-per-page .v-field[aria-expanded="true"]'
    )
    ?.closest?.('.v-data-table-footer__items-per-page')

  if (!footerSelect) return

  const input = footerSelect.querySelector?.('input[role="combobox"]')

  if (!input) return

  input.dispatchEvent(
    new KeyboardEvent('keydown', {
      bubbles: true,
      cancelable: true,
      code: 'Escape',
      key: 'Escape',
      keyCode: 27,
      which: 27
    })
  )
  input.blur()
}

function closeLegacyFooterSelectFromMenu(event) {
  const selectedItem = event.target?.closest?.(
    '.v-overlay__content.v-select__content .v-list-item'
  )

  if (!selectedItem) return

  window.setTimeout(closeLegacyFooterSelect, 150)
}

function renderSortIcon(sortable) {
  return sortable
    ? h('i', {
        class: [
          'v-icon',
          'notranslate',
          'v-data-table-header__icon',
          'mdi',
          'mdi-arrow-up',
          'theme--light'
        ],
        style: {
          fontSize: '18px'
        },
        'aria-hidden': 'true'
      })
    : null
}

function renderLegacyExpandCell(scope = {}) {
  const internalItem = scope.internalItem ?? scope.item
  const expanded = scope.isExpanded?.(internalItem)

  return h('button', {
    type: 'button',
    class: [
      'v-icon',
      'notranslate',
      'v-data-table__expand-icon',
      'v-icon--link',
      'mdi',
      'mdi-chevron-down',
      'theme--light',
      {
        'v-data-table__expand-icon--active': expanded
      }
    ],
    'aria-hidden': 'true',
    onClick: (event) => {
      event?.stopPropagation?.()
      scope.toggleExpand?.(internalItem)
    }
  })
}

function renderLegacyExpandedRow(scope = {}, slot, attrs = {}) {
  const headers = normalizeHeadersForAttrs(attrs.headers, attrs)

  return h(
    'tr',
    {
      class: ['v-data-table__expanded', 'v-data-table__expanded__content']
    },
    slot({
      ...scope,
      headers,
      item: normalizeItem(scope?.item)
    })
  )
}

function renderLegacyHeaders(scope, legacyHeaders, slots, attrs) {
  const rows = scope?.headers || []
  const sortableDisabled = hasDisableSort(attrs)

  const headerRows = rows.map((row, rowIndex) =>
    h(
      'tr',
      { key: `legacy-header-row-${rowIndex}` },
      row.map((column, columnIndex) => {
        const legacyHeader = findLegacyHeader(column, legacyHeaders) || column
        const sortable = column.sortable && !sortableDisabled
        const slotName = `header.${column.key}`
        const headerSlot = slots[slotName]
        const header = normalizeHeader(legacyHeader)

        const slotChildren = headerSlot
          ? headerSlot({
              ...scope,
              column,
              header
            })
          : [h('span', header.title ?? header.text ?? '')]
        const children = [
          ...(Array.isArray(slotChildren) ? slotChildren : [slotChildren]),
          renderSortIcon(sortable)
        ]

        return h(
          'th',
          {
            key: column.key || columnIndex,
            'aria-label': sortable
              ? `${header.title ?? header.text ?? ''}: Not sorted. Activate to sort ascending.`
              : undefined,
            'aria-sort': sortable ? 'none' : undefined,
            class: [
              ...normalizeLegacyHeaderClass({
                ...column,
                ...legacyHeader,
                sortable
              })
            ],
            role: 'columnheader',
            scope: 'col',
            colspan: column.colspan,
            rowspan: column.rowspan,
            style: normalizeLegacyHeaderStyle({
              ...column,
              ...legacyHeader
            }),
            tabindex: sortable ? 0 : undefined,
            onClick: sortable
              ? (event) => scope?.toggleSort?.(column, event)
              : undefined,
            onKeydown: sortable
              ? (event) => {
                  if (event.key === 'Enter') scope?.toggleSort?.(column, event)
                }
              : undefined
          },
          children
        )
      })
    )
  )

  return headerRows
}

function renderDataTableLoader() {
  return h('div', { class: 'legacy-data-table-loader', role: 'progressbar' }, [
    h('div', { class: 'legacy-data-table-loader__bar' })
  ])
}

export default defineComponent({
  name: 'VDataTableCompat',
  compatConfig: {
    RENDER_FUNCTION: false,
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  },
  inheritAttrs: false,
  emits: ['update:expanded'],

  setup(_, { emit, slots }) {
    const attrs = useAttrs()

    onMounted(() => {
      document.addEventListener('click', closeLegacyFooterSelectFromMenu)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', closeLegacyFooterSelectFromMenu)
    })

    return () => {
      const normalizedAttrs = stripLegacyAttrs(attrs)
      const itemValue = attrs.itemValue ?? attrs.itemKey ?? attrs['item-key']
      const sortBy = normalizeSortBy(attrs)
      const headers = normalizeHeadersForAttrs(attrs.headers, attrs)

      const wrappedSlots = Object.fromEntries(
        Object.entries(slots).map(([name, slot]) => {
          if (name.startsWith('header.')) {
            return [
              name,
              (scope) =>
                slot({
                  ...scope,
                  header: normalizeHeader(scope?.column ?? scope?.header)
                })
            ]
          }

          if (name.startsWith('item.')) {
            return [
              name,
              (scope) =>
                slot({
                  ...scope,
                  item: normalizeItem(scope?.item)
                })
            ]
          }

          if (name === 'item') {
            return [
              name,
              (scope = {}) =>
                slot({
                  ...scope,
                  // Vuetify 2's whole-row #item slot gave `isExpanded` as a
                  // boolean already resolved for this row and `expand(bool)`
                  // to set it. Vuetify 3's VDataTableRows.js instead passes
                  // `isExpanded` as a function you call with the item, and
                  // `toggleExpand(item)` with no argument (it only toggles,
                  // it can't be set to an absolute state). Every caller in
                  // this codebase only ever does `expand(!isExpanded)` (a
                  // pure toggle), so mapping straight to toggleExpand is
                  // equivalent.
                  isExpanded: scope.isExpanded?.(
                    scope.internalItem ?? scope.item
                  ),
                  expand: () =>
                    scope.toggleExpand?.(scope.internalItem ?? scope.item)
                })
            ]
          }

          if (name === 'expanded-item') {
            return [
              'expanded-row',
              (scope) => renderLegacyExpandedRow(scope, slot, attrs)
            ]
          }

          if (name === 'body.append') {
            return [
              name,
              (scope) =>
                slot({
                  ...scope,
                  headers
                })
            ]
          }

          return [name, slot]
        })
      )

      if (!slots.headers && hasLegacyHeaderPresentation(headers)) {
        wrappedSlots.headers = (scope) =>
          renderLegacyHeaders(scope, headers, slots, attrs)
      }

      if (!slots.loader) {
        wrappedSlots.loader = (scope) => renderDataTableLoader(scope, attrs)
      }

      if (hasShowExpand(attrs) && !slots['item.data-table-expand']) {
        wrappedSlots['item.data-table-expand'] = renderLegacyExpandCell
      }

      return h(
        VDataTable,
        {
          ...normalizedAttrs,
          ...normalizeDataTableFooterAttrs(attrs),
          class: normalizeDataTableClass(attrs),
          headers,
          ...(itemValue ? { itemValue } : {}),
          ...(sortBy ? { sortBy } : {}),
          ...(shouldForwardLegacyExpanded(attrs)
            ? { expanded: attrs.expanded }
            : {}),
          rowProps: (scope) => normalizeLegacyRowProps(scope, attrs),
          onClick: (event) => {
            callLegacyHandler(normalizedAttrs.onClick, event)
            openLegacyFooterSelect(event)
          },
          'onUpdate:expanded': (value) => emit('update:expanded', value)
        },
        wrappedSlots
      )
    }
  }
})
